/**
 * Shipping Policy template — renderer + editor + default data.
 */

import { useState } from "react";
import { ChevronDown, ChevronUp, Plus, Trash2 } from "lucide-react";

export const SHIPPING_DEFAULT = {
  hero: {
    eyebrow: "Delivery information",
    headline: "Our Shipping Policy",
    hl_accent: "Shipping",
    lead: "From carefully packed orders to doorstep delivery, here's how shipping works when you shop with us.",
    pills: ["📦 Secure packaging", "🚚 Delivery across serviceable India", "📍 Tracking updates"],
    bubble1: "✓ Order packed with care",
    bubble2: "📦 On its way to you",
  },
  highlights: {
    eyebrow: "What to expect",
    headline: "Shipping made simple",
    body: "We aim to make every delivery straightforward, with clear order information and helpful support.",
    cards: [
      { icon: "⚡", title: "Order Processing", text: "Confirmed orders are prepared for dispatch based on stock availability and operational schedules." },
      { icon: "📦", title: "Careful Packaging", text: "Items are packed with suitable protection to reduce damage during transit." },
      { icon: "🔎", title: "Tracking Information", text: "When tracking is available, shipment updates help you follow your parcel's progress." },
    ],
  },
  journey: {
    eyebrow: "How it works",
    headline: "Your order's journey",
    body: "Five easy stages, from checkout to your doorstep.",
    steps: [
      { title: "Order placed", desc: "Complete checkout and receive order confirmation." },
      { title: "Processing", desc: "We verify and prepare the items in your order." },
      { title: "Packed", desc: "Products are securely packed for shipping." },
      { title: "Dispatched", desc: "The shipment is handed to the assigned carrier." },
      { title: "Delivered", desc: "The carrier attempts delivery to your address." },
    ],
  },
  details: {
    eyebrow: "Good to know",
    headline: "Shipping information",
    body: "Practical details about charges, timing, delivery partners, and tracking.",
    info: [
      { icon: "⏱️", title: "Processing & dispatch", text: "Processing time depends on order verification, product availability, and working days. The estimated dispatch or delivery date, when provided at checkout or in your order updates, is the best guide for your order." },
      { icon: "📍", title: "Delivery estimates", text: "Transit time depends on your PIN code, carrier serviceability, weather, holidays, and other logistics conditions. Delivery estimates are indicative, not guaranteed arrival dates." },
      { icon: "💳", title: "Shipping charges", text: "Any applicable shipping fee is shown during checkout before you place your order. Charges may vary based on the product, order value, weight, and delivery location." },
      { icon: "🚚", title: "Logistics partners", text: "Orders may be shipped through Amazon Shipping or another available logistics provider, depending on serviceability and operational requirements. The assigned carrier will be shown when available." },
      { icon: "📲", title: "Tracking your order", text: "After dispatch, tracking information may be shared through your order page, email, or other registered contact channel. Tracking events may take time to appear after handover." },
      { icon: "🛡️", title: "Packaging & delivery issues", text: "If you receive a damaged or incorrect product, contact us within 24 hours of receipt with your order details and clear photos. Eligibility is governed by our Return & Refund Policy and applicable law." },
    ],
    callout: "This page describes our general shipping approach. Any specific delivery estimate, shipping fee, or service availability shown for your order at checkout or in order communications takes precedence over general examples.",
  },
  notes: {
    eyebrow: "Please read",
    headline: "Important delivery notes",
    body: "These small steps can help prevent avoidable delivery issues.",
    items: [
      { bold: "✓ Check your address.", text: "Enter a complete address, accurate PIN code, and reachable phone number at checkout." },
      { bold: "✓ Keep your phone available.", text: "A delivery partner may contact you to locate the address or coordinate delivery." },
      { bold: "✓ Expect occasional delays.", text: "Severe weather, local restrictions, holidays, and carrier disruptions may affect delivery." },
      { bold: "✓ Report an issue promptly.", text: "For a damaged or incorrect item, contact support within 24 hours of receiving the package." },
    ],
  },
  support: {
    eyebrow: "We're here to help",
    headline: "Questions about your delivery?",
    body: "Contact our support Monday to Saturday, 09:00–18:00 IST.",
    btn1_text: "✉ Email support",
    btn1_href: "mailto:support@importrend.com?subject=Shipping%20Support",
    btn2_text: "☎ 7011608563",
    btn2_href: "tel:+917011608563",
  },
};

const SP_CSS = `
.sp-root{--orange:#f47729;--dark:#3c3029;--muted:#74695f;--cream:#fff8f0;--peach:#ffead7;--line:#f2dfcd;--green:#367e59;color:var(--dark);background:#fffdfb;font-family:Inter,ui-sans-serif,system-ui,-apple-system,"Segoe UI",sans-serif;line-height:1.65}
.sp-root *{box-sizing:border-box}
.sp-root a{color:inherit}
.sp-root h1,.sp-root h2,.sp-root h3,.sp-root p{margin-top:0}
.sp-wrap{max-width:1180px;margin:auto;padding:0 24px}
.sp-eyebrow{display:inline-block;color:#b95419;font-size:.78rem;letter-spacing:.13em;font-weight:800;text-transform:uppercase;margin-bottom:10px}
.sp-hero{background:radial-gradient(circle at 85% 10%,#ffe1c6 0,transparent 42%),linear-gradient(125deg,#fff7ee,#fff1e2);padding:78px 0 66px;overflow:hidden}
.sp-hero-grid{display:grid;grid-template-columns:1.1fr .9fr;align-items:center;gap:55px}
.sp-hero h1{font-size:clamp(2.7rem,5vw,4.8rem);letter-spacing:-.055em;line-height:1.09;margin:14px 0 20px}
.sp-hero h1 em{font-style:normal;color:var(--orange)}
.sp-lead{font-size:1.1rem;color:var(--muted);max-width:570px;margin-bottom:0}
.sp-pills{display:flex;gap:10px;flex-wrap:wrap;margin-top:28px}
.sp-pill{padding:9px 15px;border-radius:99px;background:#fff;border:1px solid #f4dcc6;font-size:.86rem;font-weight:700}
.sp-art{position:relative;min-height:320px;border-radius:34px;background:linear-gradient(145deg,#ffd4aa,#fff1df);display:grid;place-items:center;box-shadow:0 25px 65px #c77d3924}
.sp-truck{font-size:clamp(110px,14vw,190px);filter:drop-shadow(0 12px 8px #aa683b30)}
.sp-bubble{position:absolute;background:#fff;border-radius:17px;padding:11px 16px;font-size:.86rem;font-weight:800;box-shadow:0 12px 35px #7c492122}
.sp-bubble.one{top:28px;left:20px}.sp-bubble.two{bottom:30px;right:20px}
.sp-nav{position:sticky;top:0;z-index:10;background:#ffffffed;backdrop-filter:blur(10px);border-bottom:1px solid var(--line)}
.sp-nav .sp-wrap{display:flex;gap:8px;overflow:auto;scrollbar-width:none;padding-top:12px;padding-bottom:12px}
.sp-nav a{flex:none;text-decoration:none;padding:9px 14px;border-radius:99px;font-size:.84rem;font-weight:700;color:#695449}
.sp-nav a:hover{background:var(--peach)}
.sp-content{padding:78px 0}
.sp-tint{background:var(--cream)}
.sp-sh{text-align:center;max-width:760px;margin:0 auto 37px}
.sp-sh h2{font-size:clamp(1.9rem,3.5vw,2.8rem);letter-spacing:-.045em;line-height:1.18;margin:9px 0 14px}
.sp-sh p{color:var(--muted);margin:0}
.sp-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:18px}
.sp-card{background:white;border:1px solid var(--line);border-radius:23px;padding:27px;box-shadow:0 8px 30px #a75c1610}
.sp-icon{display:inline-grid;place-items:center;width:55px;height:55px;background:#fff0e0;border-radius:16px;font-size:1.65rem;margin-bottom:13px}
.sp-card h3{font-size:1.16rem;margin:4px 0 9px}
.sp-card p{color:var(--muted);font-size:.95rem;margin:0}
.sp-steps{display:grid;grid-template-columns:repeat(5,1fr);gap:13px}
.sp-step{background:#fff;border:1px solid var(--line);border-radius:20px;padding:21px 17px;position:relative}
.sp-step-num{width:36px;height:36px;border-radius:50%;background:var(--orange);color:white;font-weight:900;display:grid;place-items:center;margin-bottom:17px}
.sp-step h3{font-size:1rem;margin:0 0 7px}
.sp-step p{font-size:.86rem;color:var(--muted);margin:0}
.sp-info-grid{display:grid;grid-template-columns:repeat(2,1fr);gap:20px}
.sp-info{border:1px solid var(--line);border-radius:24px;padding:29px;background:#fff}
.sp-info h3{margin:0 0 10px;font-size:1.2rem}
.sp-info p{margin:0;color:var(--muted)}
.sp-callout{margin-top:22px;border-radius:22px;padding:25px;background:#fff0df;border:1px solid #f5d8b8;color:#70452e}
.sp-note-list{display:grid;grid-template-columns:repeat(2,1fr);gap:15px;margin-top:28px}
.sp-note{padding:19px 22px;background:#fff;border:1px solid var(--line);border-radius:16px;color:#655b52}
.sp-note b{color:var(--dark)}
.sp-banner{display:grid;grid-template-columns:1fr auto;align-items:center;gap:20px;background:linear-gradient(110deg,#f57529,#ed9556);border-radius:28px;padding:36px 42px;color:white}
.sp-banner h2{margin:0 0 8px;font-size:clamp(1.7rem,3vw,2.4rem)}
.sp-banner p{margin:0;color:#fff5ec}
.sp-btns{display:flex;gap:11px;flex-wrap:wrap}
.sp-btn{display:inline-block;padding:12px 19px;border-radius:12px;background:white;color:#a44714;text-decoration:none;font-weight:800;white-space:nowrap}
.sp-btn-outline{border:1px solid rgba(255,255,255,.6);background:rgba(255,255,255,.1);color:white}
@media(max-width:900px){.sp-hero-grid{grid-template-columns:1fr;gap:30px}.sp-art{min-height:250px}.sp-grid{grid-template-columns:repeat(2,1fr)}.sp-steps{grid-template-columns:repeat(2,1fr)}.sp-banner{grid-template-columns:1fr}}
@media(max-width:600px){.sp-wrap{padding:0 17px}.sp-hero{padding:49px 0}.sp-hero h1{font-size:2.7rem}.sp-art{min-height:205px}.sp-truck{font-size:115px}.sp-content{padding:52px 0!important}.sp-grid,.sp-steps,.sp-info-grid,.sp-note-list{grid-template-columns:1fr}.sp-card,.sp-info{padding:23px}.sp-step{padding:21px}.sp-banner{padding:28px 22px}.sp-btns{flex-direction:column}.sp-btn{text-align:center}.sp-sh{text-align:left}.sp-sh h2{font-size:2rem}}
`;

export function ShippingPolicyRenderer({ data }) {
  const d  = data || SHIPPING_DEFAULT;
  const h  = d.hero       || SHIPPING_DEFAULT.hero;
  const hi = d.highlights || SHIPPING_DEFAULT.highlights;
  const j  = d.journey    || SHIPPING_DEFAULT.journey;
  const det = d.details   || SHIPPING_DEFAULT.details;
  const n  = d.notes      || SHIPPING_DEFAULT.notes;
  const s  = d.support    || SHIPPING_DEFAULT.support;

  const renderHeadline = (txt, accent) => {
    if (!accent || !txt.includes(accent)) return <>{txt}</>;
    const parts = txt.split(accent);
    return <>{parts[0]}<em>{accent}</em>{parts.slice(1).join(accent)}</>;
  };

  return (
    <div className="sp-root">
      <style>{SP_CSS}</style>

      <div className="sp-hero">
        <div className="sp-wrap sp-hero-grid">
          <div className="reveal">
            <span className="sp-eyebrow">{h.eyebrow}</span>
            <h1>{renderHeadline(h.headline, h.hl_accent)}</h1>
            <p className="sp-lead">{h.lead}</p>
            <div className="sp-pills">
              {(h.pills || []).map((p, i) => <span key={i} className="sp-pill">{p}</span>)}
            </div>
          </div>
          <div className="sp-art" role="img" aria-label="Illustration of a delivery truck">
            <span className="sp-bubble one">{h.bubble1}</span>
            <span className="sp-truck">🚚</span>
            <span className="sp-bubble two">{h.bubble2}</span>
          </div>
        </div>
      </div>

      <nav className="sp-nav" aria-label="On this page">
        <div className="sp-wrap">
          <a href="#sp-highlights">Highlights</a>
          <a href="#sp-journey">Order journey</a>
          <a href="#sp-details">Shipping details</a>
          <a href="#sp-notes">Important notes</a>
          <a href="#sp-support">Support</a>
        </div>
      </nav>

      <section className="sp-content" id="sp-highlights">
        <div className="sp-wrap">
          <div className="sp-sh">
            <span className="sp-eyebrow">{hi.eyebrow}</span>
            <h2>{hi.headline}</h2>
            <p>{hi.body}</p>
          </div>
          <div className="sp-grid">
            {(hi.cards || []).map((c, i) => (
              <article key={i} className="sp-card">
                <div className="sp-icon">{c.icon}</div>
                <h3>{c.title}</h3>
                <p>{c.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="sp-content sp-tint" id="sp-journey">
        <div className="sp-wrap">
          <div className="sp-sh">
            <span className="sp-eyebrow">{j.eyebrow}</span>
            <h2>{j.headline}</h2>
            <p>{j.body}</p>
          </div>
          <div className="sp-steps">
            {(j.steps || []).map((s, i) => (
              <article key={i} className="sp-step">
                <div className="sp-step-num">{i + 1}</div>
                <h3>{s.title}</h3>
                <p>{s.desc}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="sp-content" id="sp-details">
        <div className="sp-wrap">
          <div className="sp-sh">
            <span className="sp-eyebrow">{det.eyebrow}</span>
            <h2>{det.headline}</h2>
            <p>{det.body}</p>
          </div>
          <div className="sp-info-grid">
            {(det.info || []).map((item, i) => (
              <article key={i} className="sp-info">
                <h3>{item.icon} {item.title}</h3>
                <p>{item.text}</p>
              </article>
            ))}
          </div>
          {det.callout && <div className="sp-callout"><strong>Important:</strong> {det.callout}</div>}
        </div>
      </section>

      <section className="sp-content sp-tint" id="sp-notes">
        <div className="sp-wrap">
          <div className="sp-sh">
            <span className="sp-eyebrow">{n.eyebrow}</span>
            <h2>{n.headline}</h2>
            <p>{n.body}</p>
          </div>
          <div className="sp-note-list">
            {(n.items || []).map((item, i) => (
              <div key={i} className="sp-note"><b>{item.bold}</b> {item.text}</div>
            ))}
          </div>
        </div>
      </section>

      <section className="sp-content" id="sp-support">
        <div className="sp-wrap">
          <div className="sp-banner">
            <div>
              <span className="sp-eyebrow" style={{color:"#fff4e9"}}>{s.eyebrow}</span>
              <h2>{s.headline}</h2>
              <p>{s.body}</p>
            </div>
            <div className="sp-btns">
              <a className="sp-btn" href={s.btn1_href}>{s.btn1_text}</a>
              <a className="sp-btn sp-btn-outline" href={s.btn2_href}>{s.btn2_text}</a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function Field({ label, value, onChange, multiline = false, rows = 3 }) {
  const cls = "w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:border-orange-400 bg-white resize-none";
  return (
    <div>
      <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1">{label}</label>
      {multiline
        ? <textarea rows={rows} value={value || ""} onChange={e => onChange(e.target.value)} className={cls} />
        : <input type="text" value={value || ""} onChange={e => onChange(e.target.value)} className={cls} />}
    </div>
  );
}

function SectionBlock({ title, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden">
      <button type="button" onClick={() => setOpen(v => !v)}
        className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 hover:bg-slate-100 transition-colors text-left">
        <span className="text-xs font-bold text-slate-700">{title}</span>
        {open ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
      </button>
      {open && <div className="px-4 py-4 space-y-3 bg-white">{children}</div>}
    </div>
  );
}

export function ShippingPolicyEditor({ data, onChange }) {
  const d = data || SHIPPING_DEFAULT;
  const set = (section, field, value) => onChange({ ...d, [section]: { ...(d[section] || {}), [field]: value } });
  const setCard = (section, idx, field, value) => {
    const arr = [...(d[section]?.cards || d[section]?.info || d[section]?.steps || d[section]?.items || [])];
    arr[idx] = { ...arr[idx], [field]: value };
    const key = d[section]?.cards ? "cards" : d[section]?.info ? "info" : d[section]?.steps ? "steps" : "items";
    onChange({ ...d, [section]: { ...(d[section] || {}), [key]: arr } });
  };

  const h = d.hero || {};
  const hi = d.highlights || {};
  const j = d.journey || {};
  const det = d.details || {};
  const n = d.notes || {};
  const s = d.support || {};

  return (
    <div className="space-y-3">
      <div className="bg-orange-50 border border-orange-200 rounded-xl px-4 py-3 text-xs text-orange-700">
        <strong>Shipping Policy Template</strong> — Edit each section below.
      </div>

      <SectionBlock title="1. Hero" defaultOpen>
        <Field label="Eyebrow" value={h.eyebrow} onChange={v => set("hero","eyebrow",v)} />
        <Field label="Headline" value={h.headline} onChange={v => set("hero","headline",v)} />
        <Field label="Accent word" value={h.hl_accent} onChange={v => set("hero","hl_accent",v)} />
        <Field label="Lead text" value={h.lead} onChange={v => set("hero","lead",v)} multiline />
        <Field label="Pills (comma-separated)" value={(h.pills||[]).join(", ")} onChange={v => set("hero","pills",v.split(",").map(x=>x.trim()).filter(Boolean))} />
        <div className="grid grid-cols-2 gap-2">
          <Field label="Bubble 1" value={h.bubble1} onChange={v => set("hero","bubble1",v)} />
          <Field label="Bubble 2" value={h.bubble2} onChange={v => set("hero","bubble2",v)} />
        </div>
      </SectionBlock>

      <SectionBlock title="2. Highlights (3 cards)">
        <Field label="Eyebrow" value={hi.eyebrow} onChange={v => set("highlights","eyebrow",v)} />
        <Field label="Headline" value={hi.headline} onChange={v => set("highlights","headline",v)} />
        <Field label="Body" value={hi.body} onChange={v => set("highlights","body",v)} multiline />
        {(hi.cards||[]).map((c,i) => (
          <div key={i} className="border border-slate-200 rounded-xl p-3 space-y-2">
            <div className="grid grid-cols-4 gap-2">
              <Field label="Icon" value={c.icon} onChange={v => setCard("highlights",i,"icon",v)} />
              <div className="col-span-3"><Field label="Title" value={c.title} onChange={v => setCard("highlights",i,"title",v)} /></div>
            </div>
            <Field label="Text" value={c.text} onChange={v => setCard("highlights",i,"text",v)} multiline />
          </div>
        ))}
      </SectionBlock>

      <SectionBlock title="3. Order Journey (5 steps)">
        <Field label="Eyebrow" value={j.eyebrow} onChange={v => set("journey","eyebrow",v)} />
        <Field label="Headline" value={j.headline} onChange={v => set("journey","headline",v)} />
        <Field label="Body" value={j.body} onChange={v => set("journey","body",v)} multiline />
        {(j.steps||[]).map((s,i) => (
          <div key={i} className="border border-slate-200 rounded-xl p-3 space-y-2">
            <Field label={`Step ${i+1} title`} value={s.title} onChange={v => setCard("journey",i,"title",v)} />
            <Field label="Description" value={s.desc} onChange={v => setCard("journey",i,"desc",v)} />
          </div>
        ))}
      </SectionBlock>

      <SectionBlock title="4. Shipping Details">
        <Field label="Eyebrow" value={det.eyebrow} onChange={v => set("details","eyebrow",v)} />
        <Field label="Headline" value={det.headline} onChange={v => set("details","headline",v)} />
        <Field label="Body" value={det.body} onChange={v => set("details","body",v)} multiline />
        {(det.info||[]).map((item,i) => (
          <div key={i} className="border border-slate-200 rounded-xl p-3 space-y-2">
            <div className="grid grid-cols-4 gap-2">
              <Field label="Icon" value={item.icon} onChange={v => setCard("details",i,"icon",v)} />
              <div className="col-span-3"><Field label="Title" value={item.title} onChange={v => setCard("details",i,"title",v)} /></div>
            </div>
            <Field label="Text" value={item.text} onChange={v => setCard("details",i,"text",v)} multiline />
          </div>
        ))}
        <Field label="Callout / important note" value={det.callout} onChange={v => set("details","callout",v)} multiline />
      </SectionBlock>

      <SectionBlock title="5. Important Notes">
        <Field label="Eyebrow" value={n.eyebrow} onChange={v => set("notes","eyebrow",v)} />
        <Field label="Headline" value={n.headline} onChange={v => set("notes","headline",v)} />
        <Field label="Body" value={n.body} onChange={v => set("notes","body",v)} multiline />
        {(n.items||[]).map((item,i) => (
          <div key={i} className="border border-slate-200 rounded-xl p-3 space-y-2">
            <Field label="Bold text" value={item.bold} onChange={v => setCard("notes",i,"bold",v)} />
            <Field label="Body text" value={item.text} onChange={v => setCard("notes",i,"text",v)} multiline />
          </div>
        ))}
      </SectionBlock>

      <SectionBlock title="6. Support Banner">
        <Field label="Eyebrow" value={s.eyebrow} onChange={v => set("support","eyebrow",v)} />
        <Field label="Headline" value={s.headline} onChange={v => set("support","headline",v)} />
        <Field label="Body" value={s.body} onChange={v => set("support","body",v)} multiline />
        <div className="grid grid-cols-2 gap-2">
          <Field label="Button 1 text" value={s.btn1_text} onChange={v => set("support","btn1_text",v)} />
          <Field label="Button 1 href" value={s.btn1_href} onChange={v => set("support","btn1_href",v)} />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Field label="Button 2 text" value={s.btn2_text} onChange={v => set("support","btn2_text",v)} />
          <Field label="Button 2 href" value={s.btn2_href} onChange={v => set("support","btn2_href",v)} />
        </div>
      </SectionBlock>
    </div>
  );
}
